import { v4 as native_uuidv4 } from "uuid";
import { ContentInteraction } from "./interfaces";
import { rollbarError } from "./rollbarErrors";
import { ABComponentTest } from "./interfaces";

type RetryFunc = (func_args: unknown) => Promise<string>;

export class WebHelpers {
  log(context, message): void {
    message = message || "";
    if (window.ss_show_logs) {
      console.log(
        "%c%s%cSilverseaVisitor (helpers) -> %c%s",
        "background-color: darkorange; padding: 0.5em;",
        String.fromCharCode(187),
        "background-color: #323234; color: #fcfcfd; padding: 0.5em",
        "",
        context,
        message
      );
    }
  }

  debug(context, message): void {
    message = message || "";
    if (window.ss_show_logs) {
      console.debug(
        "%c%s%cSilverseaVisitor-> %c%s",
        "background-color: darkorange; padding: 0.5em;",
        String.fromCharCode(187),
        "background-color: #323234; color: #fcfcfd; padding: 0.5em",
        "",
        context,
        message
      );
    }
  }

  async retry_func(
    func: RetryFunc,
    func_args: unknown,
    func_description: string,
    timeout_ms: number,
    retries: number
  ): Promise<unknown> {
    let retry_pause = 0;

    let result;
    for (
      let attempt = 1, of_attempts = retries, success = false;
      attempt <= of_attempts && success == false;
      attempt++
    ) {
      result = await Promise.race([
        new Promise((resolve, reject) => {
          setTimeout(function () {
            try {
              resolve(func(func_args));
            } catch (e) {
              reject(e);
            }
          }, retry_pause);
        }),
        new Promise(function (resolve, reject) {
          setTimeout(function () {
            reject(
              `${func_description} timed out (took more than ${
                timeout_ms + retry_pause
              } ms)`
            );
          }, timeout_ms + retry_pause);
        })
      ])
        .then(function (res) {
          console.log(
            `${func_description} Fetch ${attempt}/${of_attempts} success.`
          );
          success = true;
          return res;
        })
        .catch(function (e) {
          console.error(
            `${func_description} Fetch ${attempt}/${of_attempts} failed.`
          );

          if (attempt == of_attempts) {
            console.error(e);
            rollbarError(`Error: ${func_description}`, e);
          } else {
            if (retry_pause == 0) {
              retry_pause = 2000;
            } else {
              retry_pause *= 2;
            }

            console.log(`retry_pause set to ${retry_pause} ms.`);
          }
        });
    }
    return result;
  }

  get_cookie(cookie_name: string): string {
    try {
      if (document.cookie) {
        const cookie_list = document.cookie.split("; ");
        for (let i = 0; i < cookie_list.length; i++) {
          const cookie_key_value_list = cookie_list[i].split("=");
          const cookie_key_name = cookie_key_value_list.shift();
          const cookie_key_value = cookie_key_value_list.join("=");
          if (cookie_name == cookie_key_name) {
            return decodeURIComponent(cookie_key_value);
          }
        }
      }
    } catch (error) {
      rollbarError(
        "get_cookie('" + cookie_name + "') could not read cookie: ",
        error
      );
    }
    return undefined;
  }

  set_cookie(
    name: string,
    value: string,
    days?: number,
    domain = undefined
  ): void {
    value = encodeURIComponent(value);
    let expires = "";
    if (days) {
      const date = new Date();
      date.setTime(date.getTime() + days * 24 * 60 * 60 * 1000);
      expires = "; expires=" + date.toUTCString();
    }
    if (!domain) {
      domain = this.get_domain();
      if (domain != "") {
        domain = "; domain=" + this.get_domain();
      }
    } else {
      domain = "; domain=" + domain;
    }
    this.debug("DOMAIN: ", domain);
    document.cookie =
      name + "=" + (value || "") + expires + "; path=/" + domain;
  }

  delete_cookie(name: string, domain?: string, path?: string): void {
    if (typeof domain === "undefined") {
      domain = this.get_domain();
    }
    if (domain !== "") {
      domain = domain || document.domain;
    }
    path = path || "/";
    document.cookie =
      name +
      "=; expires=Thu, 01 Jan 1970 00:00:01 GMT; domain=" +
      domain +
      "; path=" +
      path;
  }

  get_clean_page(): string {
    const url_parts = document.URL.split("?")[0].split("#")[0].split("/");
    url_parts.splice(0, 2);
    return url_parts.join("/");
  }

  get_page_path(): string {
    return this.get_clean_page().split("/").splice(1).join("/");
  }

  get_domain(): string {
    const patt = new RegExp("^[0-9]{1,3}.[0-9]{1,3}.[0-9]{1,3}.[0-9]{1,3}");
    const is_ip_address = patt.test(document.domain);
    if (is_ip_address) {
      return document.domain.split("/")[0];
    } else {
      const domain_array = document.domain.split(".");
      return [
        "",
        domain_array[domain_array.length - 2],
        domain_array[domain_array.length - 1]
      ].join(".");
    }
  }

  uuidv4(): string {
    return native_uuidv4();
  }

  can_write_cookies(): boolean {
    this.set_cookie("silversea_test", "testing", 1);
    const cookie_write_success =
      this.get_cookie("silversea_test") === "testing";
    this.delete_cookie("silversea_test");
    return cookie_write_success;
  }

  set_to_array(
    set_object: Set<number | string | boolean>
  ): Array<number | string | boolean> {
    const new_array = [];
    for (let it = set_object.values(), val = null; (val = it.next().value); ) {
      new_array.push(val);
    }
    return new_array;
  }

  sets_are_equal(
    set_a: Set<number | string | boolean>,
    set_b: Set<number | string | boolean>
  ): boolean {
    if (set_a.size !== set_b.size) return false;
    return this.set_to_array(set_a).every(function (set_a_elem) {
      return set_b.has(set_a_elem);
    });
  }
}

export class SilverseaHelpers extends WebHelpers {
  get_ab_test_component_experiments(): Array<ABComponentTest> {
    const ab_test_elements = document.querySelectorAll("[abtest-experiment]");
    const ab_test_names = [];
    for (let i = 0; i < ab_test_elements.length; i++) {
      ab_test_names.push(this.get_ab_test_component_vals(ab_test_elements[i]));
    }
    return ab_test_names;
  }

  get_ab_test_component_vals(ab_test_element: Element): ABComponentTest {
    return {
      e: ab_test_element.getAttribute("abtest-experiment"),
      s: ab_test_element.getAttribute("abtest-segment")
    };
  }

  get_experiment_layout_status(): string {
    return this.search_datalayer(["is_transcoded_version"]);
  }

  get_experiment_id(): string {
    return this.get_ab_test().experiment_id;
  }

  get_experiment_segment_id(): string {
    return this.get_ab_test().segment_id;
  }

  get_version(): string {
    return this.search_datalayer(["version"]);
  }

  get_user_country(): string {
    return this.search_datalayer(["user_country"]);
  }

  get_user_language(): string {
    return this.search_datalayer(["user_language"]);
  }

  get_conversion_path(): string {
    return this.get_cookie("conversion_path");
  }

  get_conversion_path_page_type(): string {
    return this.get_cookie("conversion_path_page_type");
  }

  get_page_type(): string {
    return this.search_datalayer(["page_type"]) || this.get_track_page_type();
  }

  get_track_page_type(): string {
    return this.search_datalayer(["track_page_type"]);
  }

  get_track_current_page(): string {
    return this.search_datalayer(["track_current_page"]);
  }

  get_market(): string {
    return (
      new URLSearchParams(window.location.search).get("force_market") ||
      this.search_datalayer(["market"])
    );
  }

  get_is_new_subdomain(): boolean {
    return (
      document.location.href.split(".")[0] != document.referrer.split(".")[0]
    );
  }

  lookup_platform_market(country_iso_code: string): string {
    const lookup = {
      AD: "EMEA",
      ZZ: null,
      BI: "MEA",
      TD: "MEA",
      CC: null,
      CD: null,
      CW: null,
      DJ: "MEA",
      TL: null,
      GW: "MEA",
      IO: null,
      KP: null,
      KG: null,
      NU: null,
      PN: null,
      RW: "MEA",
      SL: "MEA",
      SB: null,
      TO: null,
      EH: null,
      AX: null,
      AI: "LAM",
      AQ: "LAM",
      AG: "LAM",
      AR: "LAM",
      AW: "LAM",
      BS: "LAM",
      BB: "LAM",
      BZ: "LAM",
      BM: "US",
      BO: "LAM",
      BR: "LAM",
      VG: "LAM",
      CA: "US",
      KY: "LAM",
      CL: "LAM",
      CX: "LAM",
      CO: "LAM",
      CR: "LAM",
      CU: "LAM",
      DM: "LAM",
      DO: "LAM",
      EC: "LAM",
      SV: "LAM",
      FK: "LAM",
      GF: "LAM",
      BL: "LAM",
      GP: "LAM",
      GS: "LAM",
      GD: "LAM",
      GU: "LAM",
      GT: "LAM",
      GY: "LAM",
      HT: "LAM",
      HN: "LAM",
      JM: "LAM",
      MQ: "LAM",
      MX: "LAM",
      MS: "LAM",
      AN: "LAM",
      NI: "LAM",
      NF: "LAM",
      PA: "LAM",
      PY: "LAM",
      PE: "LAM",
      PR: "LAM",
      KN: "LAM",
      LC: "LAM",
      PM: "LAM",
      VC: "LAM",
      SR: "LAM",
      TT: "LAM",
      TC: "LAM",
      UM: "LAM",
      VI: "LAM",
      US: "US",
      UY: "LAM",
      VE: "LAM",
      AF: "MEA",
      AL: "EMEA",
      DZ: "MEA",
      AO: "MEA",
      AM: "EMEA",
      AT: "EMEA",
      AZ: "EMEA",
      BH: "MEA GSA",
      BY: "EMEA",
      BE: "EMEA",
      BJ: "MEA",
      BT: "AP",
      BA: "EMEA",
      BW: "MEA",
      BG: "EMEA",
      BF: "MEA",
      CM: "MEA",
      CV: "MEA",
      CF: "MEA",
      KM: "MEA",
      CG: "MEA",
      CI: "MEA",
      HR: "EMEA",
      CY: "EMEA",
      CZ: "EMEA",
      DK: "EMEA",
      EG: "MEA GSA",
      GQ: "MEA",
      ER: "MEA",
      EE: "EMEA",
      ET: "MEA",
      FO: "EMEA",
      FI: "EMEA",
      FR: "EMEA",
      GA: "MEA",
      GM: "MEA",
      GE: "EMEA",
      DE: "EMEA",
      GH: "MEA",
      GI: "EMEA",
      GR: "EMEA",
      GL: "US",
      GN: "MEA",
      HU: "EMEA",
      IS: "EMEA",
      IR: "MEA GSA",
      IQ: "MEA",
      IL: "MEA GSA",
      IT: "EMEA",
      JO: "MEA GSA",
      KZ: "EMEA",
      KE: "MEA",
      KW: "MEA GSA",
      LV: "EMEA",
      LB: "MEA GSA",
      LS: "MEA",
      LR: "MEA",
      LY: "MEA",
      LI: "EMEA",
      LT: "EMEA",
      LU: "EMEA",
      MK: "EMEA",
      MG: "MEA",
      MW: "MEA",
      MV: "AP",
      ML: "MEA",
      MT: "EMEA",
      MR: "MEA",
      MU: "MEA",
      YT: "EMEA",
      MD: "EMEA",
      MC: "EMEA",
      MN: "EMEA",
      ME: "EMEA",
      MA: "MEA",
      MZ: "MEA",
      NA: "MEA",
      NL: "EMEA",
      NE: "MEA",
      NG: "MEA",
      NO: "EMEA",
      OM: "MEA GSA",
      PL: "EMEA",
      PT: "EMEA",
      QA: "MEA GSA",
      RE: "MEA",
      RO: "EMEA",
      RU: "EMEA",
      SM: "EMEA",
      ST: "MEA",
      SA: "MEA GSA",
      SN: "MEA",
      RS: "EMEA",
      SC: "MEA GSA",
      SK: "EMEA",
      SI: "EMEA",
      SO: "MEA",
      ZA: "MEA GSA",
      ES: "EMEA",
      SH: "MEA",
      SD: "MEA",
      SJ: "EMEA",
      SZ: "MEA",
      SE: "EMEA",
      CH: "EMEA",
      SY: "MEA GSA",
      TJ: "AP",
      TZ: "MEA",
      TG: "MEA",
      TN: "MEA",
      TR: "EMEA",
      TM: "EMEA",
      UG: "MEA",
      UA: "EMEA",
      AE: "MEA GSA",
      UZ: "EMEA",
      VA: "EMEA",
      WF: "AP",
      YE: "MEA",
      ZM: "MEA",
      ZW: "MEA",
      AS: "AP",
      AU: "AP",
      BD: "AP",
      BN: "AP",
      KH: "AP",
      CN: "AP",
      CK: "AP",
      FM: "AP",
      FJ: "AP",
      PF: "AP",
      TF: "AP",
      HK: "AP",
      IN: "AP",
      ID: "AP",
      JP: "AP",
      KI: "AP",
      KR: "AP",
      LA: "AP",
      MO: "AP",
      MY: "AP",
      MH: "AP",
      MM: "AP",
      NR: "AP",
      NP: "AP",
      NC: "AP",
      NZ: "AP",
      MP: "AP",
      PK: "AP",
      PW: "AP",
      PG: "AP",
      PH: "AP",
      WS: "AP",
      SG: "AP",
      LK: "AP",
      TW: "AP",
      TH: "AP",
      TK: "AP",
      TV: "AP",
      VU: "AP",
      VN: "AP",
      GG: "UK",
      IE: "UK",
      IM: "UK",
      JE: "UK",
      GB: "UK"
    };
    return lookup[country_iso_code];
  }

  get_page_cat1(): string {
    return this.search_datalayer(["page_cat1"]);
  }

  get_page_cat1_friendly(): string {
    return this.get_friendly_string(this.get_page_cat1());
  }

  get_page_cat2(): string {
    return this.search_datalayer(["page_cat2"]);
  }

  get_page_cat2_friendly(): string {
    return this.get_friendly_string(this.get_page_cat2());
  }

  get_my_silversea_vals(): { [key: string]: string | number | boolean } {
    return this.search_datalayer(["my_silversea"]);
  }

  is_404_page(): boolean {
    return this.search_datalayer(["is404"]);
  }

  get_platform_market(): string {
    return (
      new URLSearchParams(window.location.search).get("force_market") ||
      this.search_datalayer(["user_geo_adwords"])
    );
  }

  get_destination_details(): { [key: string]: string | number | boolean } {
    const v = this.search_datalayer(["destination_details"]);
    return v ? v : {};
  }

  get_voyage_details(): { [key: string]: string | number | boolean } {
    const v = this.search_datalayer(["voyage_details"]);
    return v ? v : {};
  }

  get_ab_test(): { segment_id: string; experiment_id: string } {
    const v = this.search_datalayer(["ab_test"]);
    return v ? v : {};
  }

  get_friendly_string(val): string {
    if (val) {
      return val.toLocaleLowerCase().replace(/[^a-z0-1]+/g, "_");
    } else {
      return "none";
    }
  }

  js_event(event_name: string, event_details: any): void { // eslint-disable-line
    let details = undefined;
    if (event_details) {
      details = {
        detail: event_details
      };
    }
    window.dispatchEvent(new CustomEvent(event_name, details));
    this.log("EVENT: " + event_name, "");
  }

  trigger_event(event_name: string, event_details?: any): void { // eslint-disable-line
    window.dataLayer.push(this.trigger_event_helper(event_name, event_details));
    // console.log("trigger_event_finish")
  }

  // Creates an object that is formated as a unique event
  // This format allows GTM to access this events unique values
  // Otherwise GTM may collect details from other prior events
  // For example if an event has {'event': 'click', 'thing': 'a', value': 10}
  // and then another event {'event': 'click', 'thing': 'b'}
  // then without uniquely identifying the event name, thing 'b' will have a value
  // of 10 when it should be undefined.
  trigger_event_helper(event_name: string, event_details: any): any { // eslint-disable-line
    // console.log("trigger_event_start")
    const dl_event = {
      event: event_name
    };

    if (event_details) {
      const event_id = event_name + "-" + this.uuidv4();
      dl_event["event"] = event_id;
      dl_event[event_id] = event_details;
      dl_event["conversion_details"] = event_details;
    }

    return dl_event;
  }

  get_data_content_attribs(elem: HTMLElement): ContentInteraction {
    const content_piece = elem?.getAttribute("data-content-piece");

    const content_name = elem
      ?.closest("[data-content-name]")
      ?.getAttribute("data-content-name");

    if (content_name === undefined) {
      throw new Error(
        "data-content-name is required for content_piece '" +
          content_piece +
          "'"
      );
      return undefined;
    }

    const content_target =
      elem?.closest("[href]")?.getAttribute("href") ||
      elem?.getAttribute("data-content-target");
    const content_params_str = elem?.getAttribute("data-content-params");
    const content_params = content_params_str
      ? JSON.parse(content_params_str)
      : null;

    // if (content_params) {
    //   content_params = JSON.parse(content_params);
    // } else {
    //   content_params = undefined;
    // }
    // content_params = null;

    // the click is applied to a whole navigation menu rather than an individual item in the navigation menu.
    let data_content_value = "NAV";

    if (elem.tagName != "NAV") {
      data_content_value = elem.innerText || elem.textContent;

      if (data_content_value) {
        data_content_value = data_content_value.split("\n")[0];
      } else {
        data_content_value = undefined;
      }
    }

    return {
      "data-content-name": content_name,
      "data-content-piece": content_piece,
      "data-content-target": content_target,
      "data-content-params": content_params,
      "data-content-value": data_content_value
    };
  }

  search_datalayer(val_name_breadcrumb: Array<string>): any {// eslint-disable-line
    // Added a backup datalayer as it seems the dataLayer may be being cleared by GTM or something at times
    // This should counter that issue.
    return (
      this.search_datalayer_obj(window.dataLayer, val_name_breadcrumb) ||
      this.search_datalayer_obj(
        window.dataLayerBackup || [],
        val_name_breadcrumb
      )
    );
  }

  search_datalayer_obj(
    datalayer_obj: Array<any>, // eslint-disable-line
    val_name_breadcrumb: Array<string>
  ): any { // eslint-disable-line
    for (let i = datalayer_obj.length - 1; i >= 0; i--) {
      let res = datalayer_obj[i];

      for (let q = 0, l = val_name_breadcrumb.length; res !== undefined && q < l; q++) {
        res = res[val_name_breadcrumb[q]];
      }
      if (res !== undefined) {
        return res;
      }
    }
  }


  search_datalayer_event(event_name: string): unknown {
    const datalayer_obj = window.dataLayer || [];

    for (let i = datalayer_obj.length - 1; i >= 0; i--) {
      const obj_event_name = datalayer_obj[i]?.event;

      if (obj_event_name == event_name) {
        return datalayer_obj[i];
      }
    }
  }

  is_silversea_blog_domain(): boolean {
    return document.domain == "discover.silversea.com";
  }

  map_page_type(cat_1: string): string {
    if (document.domain == "discover.silversea.com") {
      return "blog";
    }

    if (typeof cat_1 == "undefined") {
      console.error("cat_1 is undefined: Cannot derrive page type.");
      return undefined;
    }

    const cat_1_lower = cat_1.toLowerCase();
    const map = {
      "Product Page": [
        "single destination",
        "single ship",
        "single offer",
        "single special voyage",
        "singe port",
        "single corporate incentives",
        "single expedition",
        "world cruises",
        "voyages",
        "single port",
        "cruises"
      ],
      "Index Page": [
        "all destinations",
        "all exclusive offers",
        "all expeditions",
        "all corporate incentives",
        "all ships",
        "on-board",
        "find your cruise",
        "why silversea",
        "all ashore",
        "all onboard",
        "all cruises",
        "all silversea experience"
      ],
      "Contact Form": ["raq", "rab", "sfo", "webq"],
      "Thank You Page": [
        "rab ty",
        "raq ty",
        "sfo ty",
        "email ty",
        "rac ty",
        "webq ty"
      ],
      "Itinerary Page": ["single cruise"],
      "Offer Page": ["single exclusive offer"],
      "Service Page": [
        "single ashore",
        "single onboard",
        "single public area category",
        "single dining area category",
        "single suite category",
        "single travel information"
      ],
      "Footer Service Page": ["terms and conditions"],
      "Find your cruise results": ["search page"],
      "Company Information Page": [
        "press releases",
        "careers",
        "contact us",
        "pressreleaseslist",
        "editorial pages",
        "press release",
        "benefits and fares",
        "travel professional center",
        "venetian society"
      ],
      "Landing Page": ["landingpage"],
      "Brochures Page": ["all brochures"]
    };

    for (const [page_type, cat_list] of Object.entries(map)) {
      if (cat_list.includes(cat_1_lower)) {
        return page_type;
      }
    }
    return cat_1;
  }
}
